import React, { useContext, useEffect, useState, Fragment, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Chip,
  Box,
  Tooltip,
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
// import { UserContext } from './Contexts/UserContext';
import { UserContext } from 'src/Contexts/UserContext';
import sidebarTheme from 'src/sidebarTheme';

const NavItemContent = ({ name, icon, badge, collapsed }) => {
  const theme = sidebarTheme;

  // Create a clone of the icon with modified props if needed
  const modifiedIcon = icon ? React.cloneElement(icon, {
    className: "nav-item-icon",
    // Remove hardcoded color to allow styling via CSS
    color: undefined
  }) : null;

  const content = (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      color: theme.colors.text.primary,
      transition: theme.transitions.default,
    }}>
      {icon && (
        <ListItemIcon
          id={`icon-${name?.replace(/\s+/g, '-').toLowerCase()}`}
          className="nav-item-icon-container"
          sx={{
            minWidth: 36,
            color: collapsed ? theme.colors.text.primary : theme.colors.primary.text,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: theme.transitions.default,
            '& svg': {
              transition: theme.transitions.default,
              fontSize: '25px',
              width: '25px',
              height: '25px',
              color: theme.colors.icon.default,
            }
          }}
        >
          {modifiedIcon}
        </ListItemIcon>
      )}

      {!collapsed && (
        <>
          <ListItemText
            primary={name}
            sx={{
              opacity: collapsed ? 0 : 1,
              transform: collapsed ? 'translateX(-10px)' : 'translateX(0)',
              transition: theme.transitions.default,
              '& .MuiListItemText-primary': {
                fontSize: '16px', // Main item font size
                fontWeight: 510,
                letterSpacing: '0.1px',
              }
            }}
          />
          {badge && (
            <Chip
              label={badge.text}
              size="small"
              sx={{
                ml: 'auto',
                height: 16,
                minWidth: 16,
                fontSize: '0.65rem',
                fontWeight: 500,
                background: theme.colors.primary.hover,
                border: `1px solid ${theme.colors.primary.light}`,
                color: theme.colors.primary.text,
                transition: theme.transitions.default,
                '& .MuiChip-label': {
                  px: 0.8,
                }
              }}
            />
          )}
        </>
      )}
    </Box>
  );

  return collapsed ? (
    <Tooltip title={name} placement="right">
      {content}
    </Tooltip>
  ) : content;
};

export const AppSidebarNav = ({ items, collapsed = false, onMenuItemClick, activeMenuIndex, setActiveMenuIndex, shouldResetMenus, onMenusReset }) => {
  const location = useLocation();
  const { contextUserData, setContextUserData } = useContext(UserContext);
  const theme = sidebarTheme;

  // Track open states for each menu group
  const [openStates, setOpenStates] = useState(() => {
    const savedOpenStates = sessionStorage.getItem('sidebarOpenStates');
    return savedOpenStates ? JSON.parse(savedOpenStates) : {};
  });

  useEffect(() => {
    const savedOpenStates = sessionStorage.getItem('sidebarOpenStates');

    // Only auto-open if this is truly the first visit (nothing saved yet)
    if (!savedOpenStates && items?.length > 0) {
      const allOpen = {};
      items.forEach((item, index) => {
        if (item.items) allOpen[`${index}`] = true;
      });
      setOpenStates(allOpen);
      sessionStorage.setItem('sidebarOpenStates', JSON.stringify(allOpen));
    }
  }, [items]); // runs whenever items loads in
  // Initialize open states based on the current location
  useEffect(() => {
    const newOpenStates = { ...openStates };

    const checkPathInItems = (itemsList, parentKey = '') => {
      itemsList?.forEach((item, idx) => {
        const itemKey = parentKey ? `${parentKey}-${idx}` : `${idx}`;

        // Check if this item's path is in the current URL
        if (item.to && location.pathname.startsWith(item.to)) {
          newOpenStates[itemKey] = true;
        }

        // Check sub-items recursively
        if (item.items) {
          checkPathInItems(item.items, itemKey);
        }
      });
    };

    checkPathInItems(items);
    setOpenStates(prevStates => ({
      ...prevStates,
      ...newOpenStates
    }));
  }, [location.pathname, items]);

  // Effect to handle activeMenuIndex changes
  useEffect(() => {
    if (activeMenuIndex !== null && !collapsed) {
      // Open the menu group that was clicked
      setOpenStates(prevState => ({
        ...prevState,
        [activeMenuIndex]: true
      }));
    }
  }, [activeMenuIndex, collapsed]);

  const updateOpenState = useCallback((key, isOpen) => {
    setOpenStates(prevState => {
      const newState = {
        ...prevState,
        [key]: isOpen
      };
      sessionStorage.setItem('sidebarOpenStates', JSON.stringify(newState));
      return newState;
    });
  }, []);

  const navItem = (item, index, parentKey = '') => {
    const { component, name, badge, icon, props, className, href, target, to, ...rest } = item;
    const itemKey = parentKey ? `${parentKey}-${index}` : `${index}`;
    const itemId = `nav-item-${itemKey}`;
    const handleItemClick = () => {
      if (!collapsed) {
        const menuIndex = parentKey || itemKey;
        onMenuItemClick(menuIndex);
      }

      if (rest.code?.trim() === "DSH_CNFG_BRMSG") {
        setContextUserData((prev) => ({ ...prev, showBrodCastMessage: true }));
      }
    };

    // Determine if the link is external or internal
    const isExternal = !!href;

    return (
      <ListItem
        key={itemKey}
        id={itemId}
        component={isExternal ? 'a' : NavLink}
        {...(isExternal
          ? { href, target, rel: target === '_blank' ? 'noopener noreferrer' : undefined }
          : { to })}
        onClick={handleItemClick}
        sx={{
          padding: '6px 12px 6px 12px',
          color: theme.colors.primary.textMuted,
          textDecoration: 'none',
          transition: theme.transitions.default,
          borderRadius: '6px',
          mx: collapsed ? 0.5 : 0,
          my: 0.25,
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: "inherit",
          cursor: 'pointer',
          '&.active': {
            color: theme.colors.primary.text,
            backgroundColor: theme.colors.background.hover,
            '& .nav-item-icon-container': {
              color: `${theme.colors.icon.active} !important`,
              '& svg': {
                transform: 'scale(1.05)',
                color: 'inherit !important',
              }
            }
          },
          '&:hover': {
            color: theme.colors.icon.hover,
            backgroundColor: theme.colors.background.hover,
            transform: 'translateX(4px)',
            '& .nav-item-icon-container': {
              color: `${theme.colors.icon.hover} !important`,
              '& svg': {
                transform: 'scale(1.05)',
                color: 'inherit !important',
              }
            }
          },
          // Add left border for child items
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: '2px',
            height: '0',
            backgroundColor: theme.colors.text.primary,
            transition: theme.transitions.default,
          },
          '&:hover::before': {
            height: '60%',
          },
          '&.active::before': {
            height: '60%',
          }
        }}
      >
        <NavItemContent
          name={name}
          icon={icon}
          badge={badge}
          collapsed={collapsed}
        />
      </ListItem>
    );
  };

  const getNavGroupAccentColor = (groupName) => {
    switch (groupName) {
      case 'Integrated Module':
        return '#5B9BD5';
      case 'Dashboard Module (SSO)':
        return '#FD6929';
      case 'R&BD Links':
        return '#00B050';
      default:
        return null;
    }
  };

  const navGroup = (item, index, parentKey = '') => {
    const { name, icon, to, items: subItems } = item;
    const itemKey = parentKey ? `${parentKey}-${index}` : `${index}`;
    const groupId = `nav-group-${itemKey}`;

    // Check if this group is open
    const isOpen = openStates[itemKey] || false;

    const accentColor = getNavGroupAccentColor(name);
    // For accent groups: solid fill. For others: existing theme behavior.
    const bgColor = accentColor
      ? accentColor
      : isOpen
        ? theme.colors.background.hover
        : 'inherit';
    // Darken the accent color slightly on hover by overlaying black at 15%
    const hoverBgColor = accentColor
      ? accentColor   // keep same, brightness handled via filter below
      : theme.colors.background.navGroupHover;
    const accentTextColor = accentColor ? '#ffffff' : theme.colors.primary.textMuted;
    const accentIconColor = accentColor ? '#ffffff' : theme.colors.icon.hover;

    const handleGroupClick = () => {
      if (collapsed) {
        // If sidebar is collapsed, tell the parent to open it and this menu
        onMenuItemClick(itemKey);
      } else {
        // If sidebar is open, toggle this menu's open state
        updateOpenState(itemKey, !isOpen);
      }
    };

    return (
      <Fragment key={itemKey}>
        <ListItem
          button
          id={groupId}
          onClick={handleGroupClick}
          className="nav-group-item"
          sx={{
            padding: '10px 12px',
            color: accentTextColor,
            borderRadius: '8px',
            mx: collapsed ? 0.5 : 1,
            my: 0.5,
            transition: theme.transitions.default,
            backgroundColor: bgColor,
            cursor: 'pointer',
            border: `1px solid ${accentColor ? 'transparent' : (isOpen ? theme.colors.primary.light : 'transparent')}`,
            ...(accentColor ? { filter: 'brightness(1)' } : {}),
            '&:hover': {
              color: accentColor ? '#ffffff' : theme.colors.primary.text,
              backgroundColor: hoverBgColor,
              filter: accentColor ? 'brightness(0.88)' : 'none',
              transform: 'translateX(4px)',
              borderColor: accentColor ? 'transparent' : theme.colors.primary.light,
              '& .nav-item-icon-container': {
                color: `${accentIconColor} !important`,
                '& svg': {
                  transform: 'scale(1.05)',
                  color: `${accentIconColor} !important`,
                }
              },
              '& .MuiBox-root': {
                color: accentColor ? '#ffffff' : theme.colors.primary.text,
              },
              '& .MuiListItemText-root .MuiListItemText-primary': {
                color: accentColor ? '#ffffff' : theme.colors.primary.text,
              },
              '& .MuiSvgIcon-root': {
                color: accentColor ? '#ffffff' : theme.colors.primary.text,
              }
            },
            // Ensure icon and text are white when accent background is set
            ...(accentColor ? {
              '& .nav-item-icon-container': {
                color: '#ffffff !important',
                '& svg': { color: '#ffffff !important' }
              },
              '& .MuiListItemText-root .MuiListItemText-primary': {
                color: '#ffffff',
              },
              '& .MuiSvgIcon-root': {
                color: '#ffffff',
              }
            } : {}),
          }}
        >
          <NavItemContent
            name={name}
            icon={icon}
            badge={null}
            collapsed={collapsed}
          />
          {!collapsed && (
            <Box
              component={isOpen ? ExpandMoreIcon : ChevronRightIcon}
              sx={{
                ml: 'auto',
                transition: theme.transitions.default,
                transform: isOpen ? 'rotate(0deg)' : 'rotate(0deg)',
                opacity: 0.5,
                fontSize: '1rem',
                color: theme.colors.text.primary,
              }}
            />
          )}
        </ListItem>
        {!collapsed && (
          <Collapse
            in={isOpen}
            timeout={250}
            unmountOnExit
            sx={{
              transition: theme.transitions.default,
              backgroundColor: accentColor ? accentColor : theme.colors.background.expandedHover,
              borderRadius: '8px',
              // width: "95%",
              ml: "15px",
              mr: "0px",
              mb: 0.5,
            }}
          >
            <List
              component="div"
              disablePadding
              sx={{
                '& .MuiListItem-root': {
                  pl: 2,
                  fontSize: '14px',
                  '& .MuiListItemText-primary': {
                    fontSize: '14px',
                  }
                }
              }}
            >
              {subItems?.map((subItem, subIndex) =>
                subItem.items ?
                  navGroup(subItem, subIndex, itemKey) :
                  navItem(subItem, subIndex, itemKey)
              )}
            </List>
          </Collapse>
        )}
      </Fragment>
    );
  };

  return (
    <List sx={{
      padding: '6px 0',
      '& .MuiListItemIcon-root': {
        color: theme.colors.primary.textMuted,
        minWidth: collapsed ? 36 : 36,
        transition: theme.transitions.default,
      }
    }}>
      {items?.map((item, index) => (
        item.items ? navGroup(item, index) : navItem(item, index)
      ))}
    </List>
  );
};

export default AppSidebarNav;